import { FC, ReactNode, useMemo } from "react"
import { useTranslation } from "react-i18next"

import { IListItem, ListItemType } from "components/List/List"
import { ArticleAny, ISummarySection } from "@/types"
import Summary from "components/Summary/Summary"
import useAssetFields, { AssetField } from "../../hooks/useAssetFields"
import Asset from "models/Asset"
import { useAssetPinnedFields } from "../../stores/assetPinnedFields"
import ConnectedAsset from "models/ConnectedAsset"
import AssetDisplayStatus from "models/AssetDisplayStatus"
import { IUseAssetInitialSaleOrRental } from "hooks/useAssetInitialSaleOrRental"
import Color from "models/Color"
import useCopyFeatures from "hooks/useCopyFeatures"
import Address from "models/Address"
import { IAssetInternalNotesEdit } from "../AssetData/AssetDataContext"
import Button from "components/Button/Button"
import Input from "components/Input/Input"
import NoValue from "components/NoValue/NoValue"
import { Flex } from "components/Flex"
import "../AssetData/AssetDataOrganisationalAttributes.scss"

interface IAssetPinnerFieldsProps {
    asset: Asset
    article: ArticleAny
    connectedAssets?: ConnectedAsset[]
    displayStatus: AssetDisplayStatus | undefined
    displayStatusLoading: boolean
    homebaseWarehouseDescription?: string | null
    initialSaleOrRental?: IUseAssetInitialSaleOrRental
    eventAddress?: Address
    operationHoursLogButton: ReactNode
    color: Color | null
    internalNotesEdit?: IAssetInternalNotesEdit
}

const AssetPinnedFields: FC<IAssetPinnerFieldsProps> = ({
    asset,
    article,
    connectedAssets,
    displayStatus,
    displayStatusLoading,
    homebaseWarehouseDescription,
    initialSaleOrRental,
    eventAddress,
    operationHoursLogButton,
    color,
    internalNotesEdit,
}) => {
    const { t } = useTranslation()
    const { pinnedFields } = useAssetPinnedFields()
    const { onCopyFeatures } = useCopyFeatures({ asset, article })
    const { getField } = useAssetFields({
        asset,
        article,
        connectedAssets,
        displayStatus,
        displayStatusLoading,
        homebaseWarehouseDescription,
        initialSaleOrRental,
        operationHoursLogButton,
        onCopyFeatures,
        eventAddress,
        color,
        pinnable: true,
        pinnedBlock: true,
    })

    const summary: ISummarySection<IListItem> = useMemo(() => {
        const items: IListItem[] = []
        if (!pinnedFields || !pinnedFields.length) return { items }
        for (const field of pinnedFields) {
            if (field === AssetField.internalNotes && internalNotesEdit?.canEdit) {
                const baseItem = getField(field)
                if (!baseItem) continue
                const isPinnedEditing = internalNotesEdit.editingContext === 'pinned'
                items.push({
                    ...baseItem,
                    type: ListItemType.verticalSection,
                    value: isPinnedEditing ? (
                        <Input
                            type="textarea"
                            value={internalNotesEdit.notesValue}
                            onChange={(e) => internalNotesEdit.setNotesValue(e.target.value)}
                            onBlur={internalNotesEdit.handleBlur}
                            autoFocus
                            maxLength={2500}
                        />
                    ) : (
                        <div className="mb-2">{internalNotesEdit.savedNotes || <NoValue>{t('asset.noNotes')}</NoValue>}</div>
                    ),
                    toolbar: !isPinnedEditing && internalNotesEdit.editingContext !== 'section' ? (
                        <Button
                            label={t('common.edit')}
                            theme="link"
                            onClick={() => internalNotesEdit.startEdit('pinned')}
                        />
                    ) : isPinnedEditing ? (
                        <div onPointerDown={internalNotesEdit.onToolbarPointerDown}>
                            <Flex gap="0.8rem" alignItems="center">
                                <Button label={t('common.save')} theme="link" onClick={internalNotesEdit.handleSave} />
                                <div className="asset-data-organisational-attributes__toolbar-divider" />
                                <Button label={t('common.cancel')} theme="link" onClick={internalNotesEdit.handleCancel} />
                            </Flex>
                        </div>
                    ) : undefined,
                })
            } else {
                const item = getField(field)
                if (!item) continue
                items.push(item)
            }
        }
        return { items }
    }, [getField, pinnedFields, internalNotesEdit, t])

    if (!summary.items.length) return null

    return (
        <div className="px-1-5 py-3 bg-grey-50 round">
            <Summary data={[summary]} />
        </div>
    )
}

export default AssetPinnedFields
